import React, { useCallback, useEffect, useRef } from "react";
import BackgroundRouter from "../../components/background-page";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
} from "@mui/material";
import { Body1, Body2, EntradaForm, H6, Icone } from "../../components";
import useFetch from "../../hooks/use-fetch";
import { ToastErro } from "../../utils/utilitarios";
import {
  AnimacaoCashback,
  SemDados,
  Parabens,
} from "../../components/sem-pesquisa";
import { API_KEY } from "../../config_api";
import * as yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { Utilitarios } from "../../utils";
import { blue, green, red } from "@mui/material/colors";

const APP_DEBUG =
  window.location.hostname.search("localhost") !== -1 ||
  window.location.hostname.search(/^10\.5\.*/g) !== -1;

const STR = {
  titlePage: "Consulta de Cashback",
  subtitle: "Você tem cashback ativo conosco 🤔 ?",
  subtitle2:
    "Aqui você pode consultar o seus cashbacks 😊, basta digitar o seu CPF abaixo.",
  titleSuccess: "Você tem cashback ativo conosco",
  subtitleSuccess: "Abaixo os dados do seu cashback",
  subtitleSuccess2:
    "Lembrando que o crédito é ativado desde que o valor seja 3 vezes maior que o Cashback adquirido.",
  subtitleSuccess3:
    "*Não é válido para compras de lentes de contato e não é acumulativo com demais promoções* consulte regulamento.",
  titleWithoutCashback: "Que pena, você não tem cashback ativo conosco 😢.",
  subtitleWithoutCashback:
    "Mas não fique triste, venha comprar com agente e ganhar o seu cashback 🤑.",
  labelCgc: "CPF",
  placeholderCgc: "Digite o seu CPF",
  errorCgc: "* O CPF não esta no formato 000.000.000-00",
  newQuery: "Nova Consulta",
  valueCashback: "Valor do Cashback",
  prazToUse: "Prazo para utilização",
  addressStore: "Endereço da Loja",
  important: "⚠️ REGRAS DA PROMOÇÃO",
};

const KEYS = {
  cgc: "cgc",
  captcha: "captcha",
};

const getCaptcha = async (recaptchaRef) => {
  if (!recaptchaRef.current) return "no-captcha";
  await recaptchaRef.current.reset();
  // Pega o token do recaptcha
  let TOKEN = recaptchaRef.current.getValue();
  TOKEN = await recaptchaRef.current.executeAsync();
  return TOKEN;
};

const StackCenter = ({ children }) => {
  return (
    <Stack
      sx={{
        maxHeight: "calc(100vh - 116px)",
        height: "calc(100vh - 116px)",
        justifyContent: "center",
      }}
    >
      {children}
    </Stack>
  );
};

const ConsultaCashbackCliente = () => {
  // Para referenciar o captcha invisivel
  const recaptchaRef = useRef();
  const { data, wait, error, setFetch, clearData } = useFetch(
    "/consulta_cashback_cliente_api",
    "GET"
  );
  //
  useEffect(() => {
    if (error) {
      ToastErro(error);
    }
  }, [error]);
  //

  return (
    <BackgroundRouter>
      <Container maxWidth="md">
        {data === null ? (
          <Grow in unmountOnExit>
            <Box>
              <CashbackVisualizacaoInicial
                wait={wait}
                setFetch={setFetch}
                captchaRef={recaptchaRef}
              />
            </Box>
          </Grow>
        ) : data.length === 0 ? (
          <Grow in unmountOnExit>
            <Box>
              <CashbackNaoEncontrado clearData={clearData} />
            </Box>
          </Grow>
        ) : (
          <Grow in unmountOnExit>
            <Box>
              <CashbackEncontrado cashbacks={data} clearData={clearData} />
            </Box>
          </Grow>
        )}
      </Container>
      {APP_DEBUG ? null : (
        <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={API_KEY} />
      )}
    </BackgroundRouter>
  );
};

const CashbackVisualizacaoInicial = ({ setFetch, wait, captchaRef }) => {
  const schema = [
    {
      type: "text",
      name: KEYS.cgc,
      extraProps: {
        variant: "filled",
      },
      label: STR.labelCgc,
      placeholder: STR.placeholderCgc,
      required: true,
      mask: [
        /\d/,
        /\d/,
        /\d/,
        ".",
        /\d/,
        /\d/,
        /\d/,
        ".",
        /\d/,
        /\d/,
        /\d/,
        "-",
        /\d/,
        /\d/,
      ],
    },
  ];
  const schemaMessageError = {
    [KEYS.cgc]: STR.errorCgc,
  };
  const schemaValidator = yup.object().shape({
    [KEYS.cgc]: yup
      .string()
      .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, STR.errorCgc)
      .required(STR.errorCgc),
  });
  const onSubmit = useCallback(
    async (val) => {
      const captcha = captchaRef ? await getCaptcha(captchaRef) : "";
      const obj = {
        [KEYS.cgc]: val[KEYS.cgc],
        [KEYS.captcha]: captcha,
      };
      setFetch(obj);
    },
    [setFetch, captchaRef]
  );

  return (
    <StackCenter>
      <Paper elevation={3} sx={{ p: 1 }}>
        <H6>{STR.titlePage}</H6>
        <Body1 align="center">{STR.subtitle}</Body1>
        <Body1 align="center">{STR.subtitle2}</Body1>
        <EntradaForm
          schema={schema}
          schemaValidator={schemaValidator}
          schemaMessageError={schemaMessageError}
          onSubmit={onSubmit}
          wait={wait}
        />
        <AnimacaoCashback />
      </Paper>
    </StackCenter>
  );
};

const CashbackNaoEncontrado = ({ clearData }) => {
  return (
    <StackCenter>
      <Paper elevation={3} sx={{ p: 1 }}>
        <SemDados />
        <H6>{STR.titleWithoutCashback}</H6>
        <Body1 align="center">{STR.subtitleWithoutCashback}</Body1>
        <Button
          fullWidth
          startIcon={<Icone icone="Undo" />}
          variant="contained"
          onClick={clearData}
        >
          {STR.newQuery}
        </Button>
      </Paper>
    </StackCenter>
  );
};

const CashbackEncontrado = ({ cashbacks, clearData }) => {
  return (
    <Stack>
      <H6>{STR.titleSuccess}</H6>
      <Parabens />

      <Body1 align="center">{STR.subtitleSuccess}</Body1>
      <Accordion sx={{ border: `2px solid ${red[500]}` }}>
        <AccordionSummary expandIcon={<Icone icone="ExpandMore" />}>
          <Body1 sx={{ color: red[500] }} align="center">
            {STR.important}
          </Body1>
        </AccordionSummary>
        <AccordionDetails>
          <Body2 sx={{ color: red[500] }}>{STR.subtitleSuccess2}</Body2>
          <br />
          <Body2 sx={{ color: red[500] }}>{STR.subtitleSuccess3}</Body2>
        </AccordionDetails>
      </Accordion>

      {cashbacks?.map((ele, idx) => (
        <Paper sx={{ my: 1 }} elevation={4} key={idx}>
          <List disablePadding>
            <ListItem>
              <ListItemIcon>
                <Icone icone="CardGiftcard" sx={{ color: green[500] }} />
              </ListItemIcon>
              <ListItemText
                primary={Utilitarios.converter(ele.valor_voucher)}
                secondary={STR.valueCashback}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Icone icone="CalendarToday" sx={{ color: red[500] }} />
              </ListItemIcon>
              <ListItemText
                primary={Utilitarios.converterData(ele.data_prazo)}
                secondary={STR.prazToUse}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Icone icone="Store" sx={{ color: blue[500] }} />
              </ListItemIcon>
              <ListItemText
                primary={STR.addressStore}
                secondary={ele.endereco_loja}
              />
            </ListItem>
          </List>
        </Paper>
      ))}
      <Button
        fullWidth
        startIcon={<Icone icone="Undo" />}
        variant="contained"
        onClick={clearData}
      >
        {STR.newQuery}
      </Button>
      <br />
      <br />
    </Stack>
  );
};

ConsultaCashbackCliente.route = "/consulta_cashback_cliente";

export default ConsultaCashbackCliente;
